/*
  This file is part of the project: "Digiziege"
  An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
  GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

  Copyright (c) 2026 Digiziege Team TH-Bingen

  Description:
  Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
  master data management and health tracking.
  Built as a PWA with a local-first approach to ensure data privacy.

  LICENSE INFORMATION:
  This program is free software: you can redistribute it and/or modify it under the terms of the
  GNU Affero General Public License as published by the Free Software Foundation,
  either version 3 of the License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
  without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
  See the GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License along with this program.
  If not, see <https://www.gnu.org/licenses/>.
*/

import { createRouter, createWebHistory } from 'vue-router'
import HomeView from '@/views/HomeView.vue'
import GoatDetailView from '@/views/goats/GoatDetailView.vue'
import MoreView from '@/views/more/MoreView.vue'
import LanguageView from '@/views/more/LanguageView.vue'
import BackupView from '@/views/more/BackupView.vue'
import FocalLengthSettingsView from '@/views/more/FocalLengthSettingsView.vue'
import EartagPrefixSettingsView from '@/views/more/EartagPrefixSettingsView.vue'
import LegalNoticeView from '@/views/more/LegalNoticeView.vue'
import LicensesView from '@/views/more/LicensesView.vue'
import QuickLinksSettingsView from '@/views/more/QuickLinksSettingsView.vue'
import NewGoatView from '@/views/goats/NewGoatView.vue'
import NewGroupView from '@/views/groups/NewGroupView.vue'
import SessionSelectView from '@/views/session/SessionSelectView.vue'
import SessionGroupSelectView from '@/views/session/SessionGroupSelectView.vue'
import SessionGoatSelectView from '@/views/session/SessionGoatSelectView.vue'
import GroupDetailView from '@/views/groups/GroupDetailView.vue'
import WeightView from '@/views/goats/WeightView.vue'
import TemperatureView from '@/views/goats/TemperatureView.vue'
import FamachaView from '@/views/goats/FamachaView.vue'
import GeneralInfoView from '@/views/goats/GeneralInfoView.vue'
import HealthView from '@/views/goats/HealthView.vue'
import WoundsDetailView from '@/views/goats/WoundsDetailView.vue'
import TreatmentsDetailView from '@/views/goats/TreatmentsDetailView.vue'
import GoatListView from '@/views/goats/GoatListView.vue'
import GroupListView from '@/views/groups/GroupListView.vue'
import AppInfoView from '@/views/more/AppInfoView.vue'
import ActivitiesView from '@/views/home/ActivitiesView.vue'
import DagView from '@/views/goats/DagView.vue'
import NotesView from '@/views/goats/NotesView.vue'


//views for the Main Screen Bottom Menu Bar
const routes = [
  { path: '/', name: 'Home', component: HomeView }, // Temporarily disabled - under development
  // { path: '/', redirect: '/goats' }, // Default to Goats page
  { path: '/activities', name: 'Activities', component: ActivitiesView },
  { path: '/goats/:id', name: 'GoatDetail', component: GoatDetailView },
  { path: '/goats/:id/weight', name: 'Weight', component: WeightView },
  { path: '/goats/:id/temperature', name: 'Temperature', component: TemperatureView },
  { path: '/goats/:id/famacha', name: 'Famacha', component: FamachaView },
  { path: '/goats/:id/health', name: 'Health', component: HealthView },
  { path: '/goats/:id/health/wounds', name: 'Wounds', component: WoundsDetailView },
  { path: '/goats/:id/health/treatments', name: 'Treatments', component: TreatmentsDetailView },
  { path: '/goats/:id/dag', name: 'Dag', component: DagView },
  { path: '/goats/:id/notes', name: 'Notes', component: NotesView },
  { path: '/goats/:id/generalinfo', name: 'GeneralInfo', component: GeneralInfoView },
  { path: '/goats', name: 'GoatList', component: GoatListView },
  { path: '/groups', name: 'GroupList', component: GroupListView },
  { path: '/more', name: 'More', component: MoreView },
  { path: '/language', name: 'Language', component: LanguageView },
  { path: '/more/focal-length', name: 'FocalLengthSettings', component: FocalLengthSettingsView },
  { path: '/more/eartag-prefix', name: 'EartagPrefixSettings', component: EartagPrefixSettingsView },
  { path: '/more/legal-notice', name: 'LegalNotice', component: LegalNoticeView},
  { path: '/more/licenses', name: 'Licenses', component: LicensesView},
  { path: '/more/quick-links', name: 'QuickLinksSettings', component: QuickLinksSettingsView},
  { path: '/more/app-info', name: 'AppInfo', component: AppInfoView},
  { path: '/new-goat', name: 'NewGoat', component: NewGoatView },
  { path: '/new-group', name: 'NewGroup', component: NewGroupView },
  { path: '/backup', name: 'Backup', component: BackupView },
  { path: '/session-select', name: 'SessionSelect', component: SessionSelectView },
  { path: '/session/:type/groups', name: 'SessionGroupSelect', component: SessionGroupSelectView },
  { path: '/session/:type/:groupId/goats', name: 'SessionGoatSelect', component: SessionGoatSelectView },
  { path: '/groups/:groupId', name: 'GroupDetail', component: GroupDetailView },
  { path: '/:pathMatch(.*)*', redirect: '/goats' }
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    // If there's a saved position (e.g., browser back/forward), restore it
    if (savedPosition) {
      return savedPosition
    }
    // Otherwise, scroll to top for new navigation
    return { top: 0 }
  }
})

export default router
